import { Input } from '@heroui/react'
import { capitalizeFirstLetter, FIELD_LIMITS } from '@leangap/types'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

interface NameInputProps<T extends FieldValues> {
	control: Control<T>
	name: FieldPath<T>
	label: string
	isReadOnly?: boolean
	isClearable?: boolean
}

/** Controlled name input that normalizes the value as the user types or pastes. */
export default function NameInput<T extends FieldValues>({
	control,
	name,
	label,
	isReadOnly = false,
	isClearable = false,
}: NameInputProps<T>) {
	const { field, fieldState } = useController({ control, name })
	const value = typeof field.value === 'string' ? field.value : ''

	return (
		<Input
			ref={field.ref}
			name={field.name}
			label={label}
			value={value}
			onValueChange={(nextValue) => field.onChange(capitalizeFirstLetter(nextValue))}
			onBlur={field.onBlur}
			onClear={isClearable ? () => field.onChange('') : undefined}
			isReadOnly={isReadOnly}
			isClearable={isClearable && !isReadOnly}
			isRequired
			maxLength={FIELD_LIMITS.name}
			isInvalid={!!fieldState.error}
			errorMessage={fieldState.error?.message}
		/>
	)
}
